import { MutableRefObject, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Action, ActionCreator, Dispatch } from 'redux';

import { AbortPayload } from '@shared/types/redux';

// Abort
export const useDispatchAbort = <AP extends AbortPayload<any>>(
  dispatch: Dispatch<any>,
  actionCreator: ActionCreator<Action>,
  controllerRef: MutableRefObject<AbortController | null>
) =>
  useCallback(
    (payload: AP['payload']) => {
      if (controllerRef.current) {
        controllerRef.current?.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      return dispatch(actionCreator({ payload, controller }));
    },
    [actionCreator, controllerRef, dispatch]
  );

// Abort Promise
export type DispatchAbortPromise = (
  actionCreator: ActionCreator<Action>,
  controllerRef: MutableRefObject<AbortController | null>,
  payload: any
) => Promise<any>;

export const useDispatchAbortPromise = (): DispatchAbortPromise => {
  const dispatch = useDispatch();

  return (actionCreator, controllerRef, payload) => {
    if (controllerRef.current) {
      controllerRef.current?.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    return new Promise((resolve, reject) => {
      dispatch(actionCreator({ payload, controller, resolve, reject }));
    });
  };
};

// Promise
export type DispatchPromise = (actionCreator: ActionCreator<Action>, payload: any) => Promise<any>;

export const useDispatchPromise = (): DispatchPromise => {
  const dispatch = useDispatch();

  return (actionCreator, payload) =>
    new Promise((resolve, reject) => {
      dispatch(actionCreator({ payload, resolve, reject }));
    });
};
